import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useStore } from "../../Components/StateProvider";
import {
  URLPRIFIX,
  GetRecords,
  GetDateTimeFormat,
  RupeeFormatWithoutDecimal,
  formatDate,
} from "../../functions/api";
import ProfitLossChart from "../../Components/Elements/ProfitLossChart";
import CustomPieChart from "../../Components/Elements/CustomPieChart";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { 
  FaWallet, 
  FaFileInvoiceDollar, 
  FaChartLine, 
  FaArrowUp, 
  FaArrowDown, 
  FaCalendarAlt, 
  FaExclamationTriangle,
  FaBuilding
} from "react-icons/fa";
import dayjs from "dayjs";

const AccountsDashboard = () => {
  const store = useStore();
  const { companyId } = useParams();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("Yearly");
  const [dashboardData, setDashboardData] = useState({
    total_revenue: 0,
    total_expense: 0,
    net_profit: 0,
    blnc: 0,
    invoiceData: [],
    upcomingDueDates: [],
    upcomingoverdue: [],
    coreManagement: [],
  });

  const [loading, setLoading] = useState(true);

  // Fetch Dashboard data
  const fetchDashboardData = () => {
    setLoading(true);
    const dateTime = GetDateTimeFormat();
    
    // Default to start of previous financial year (2025-04-01) to capture active invoices
    let start = "2025-04-01T00:00:00Z";
    let end = dateTime.yearEndDate + "Z";
    
    const today = dayjs();
    if (selectedPeriod === "Monthly") {
      start = today.startOf("month").format("YYYY-MM-DD") + "T00:00:00Z";
      end = today.endOf("month").format("YYYY-MM-DD") + "T23:59:59Z";
    }

    const url = `/api/v2/accounting/getdashboarddata/${companyId}?startDate=${start}&endDate=${end}`;
    
    GetRecords(url)
      .then((res) => {
        if (res.success) {
          setDashboardData(res.record);
        } else {
          console.error("Failed to fetch dashboard data");
        }
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDashboardData();
  }, [companyId, selectedPeriod]);

  useEffect(() => {
    store.setmodule("companydashboard").then(() => {
      store.stopLoading();
      store.setheader("Accounting Dashboard");
    });
  }, []);

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  // Cohesive Modern Hex Color Palette
  const COLORS = {
    revenue: "#4F46E5", // Indigo
    expense: "#EF4444", // Rose/Red
    profit: "#10B981",  // Emerald Green
    balance: "#0EA5E9", // Sky Blue
    pie: ["#6366F1", "#8B5CF6", "#EC4899", "#3B82F6", "#10B981", "#F59E0B"]
  };

  // Safe mapping for Invoice Donut Chart
  const formattedInvoiceData = useMemo(() => {
    if (!dashboardData?.invoiceData || dashboardData.invoiceData.length === 0) {
      return [
        { name: "Draft/Awaiting", value: 1, color: "#E2E8F0" }
      ];
    }
    return dashboardData.invoiceData.map((item, idx) => ({
      name: item.name || "Uncategorized",
      value: Number(item.value) || 0,
      color: item.color || COLORS.pie[idx % COLORS.pie.length]
    })).filter(item => item.value > 0);
  }, [dashboardData]);

  return (
    <div className="flex flex-col gap-6 p-6 bg-[#F8FAFC] min-h-screen text-slate-800">
      
      {/* Top Welcome / Filter Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2">
            <FaBuilding className="text-indigo-600" size={18} />
            Financial Overview
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Real-time billing, revenue tracking, and account balances 
          </p>
        </div>

        {/* Period Selector Tabs */}
        <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
          {["Monthly", "Yearly"].map((period) => (
            <button
              key={period}
              onClick={() => handlePeriodChange(period)}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer ${
                selectedPeriod === period
                  ? "bg-white text-indigo-600 shadow-sm font-black"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {period}
            </button>
          ))}
        </div>
      </div>

      {/* Main KPI Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        
        {/* Total Revenue */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition duration-200">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Revenue</span>
              <h3 className="text-2xl font-black text-slate-900 mt-1 tracking-tight">
                ₹ {RupeeFormatWithoutDecimal(dashboardData.total_revenue)}
              </h3>
            </div>
            <div className="p-3.5 rounded-xl bg-indigo-50 text-indigo-600">
              <FaChartLine size={16} />
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-50 flex items-center gap-1.5 text-[11px] text-emerald-600 font-bold">
            <FaArrowUp size={10} />
            <span>Invoices & Sales</span>
          </div>
        </div>

        {/* Total Expense */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition duration-200">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Expense</span>
              <h3 className="text-2xl font-black text-rose-600 mt-1 tracking-tight">
                ₹ {RupeeFormatWithoutDecimal(dashboardData.total_expense)}
              </h3>
            </div>
            <div className="p-3.5 rounded-xl bg-rose-50 text-rose-600">
              <FaWallet size={16} />
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-50 flex items-center gap-1.5 text-[11px] text-rose-500 font-bold">
            <FaArrowDown size={10} />
            <span>Operational Costs</span>
          </div>
        </div>

        {/* Net Profit */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition duration-200">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Net Profit / Loss</span>
              <h3 className={`text-2xl font-black mt-1 tracking-tight ${dashboardData.net_profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                ₹ {RupeeFormatWithoutDecimal(dashboardData.net_profit)}
              </h3>
            </div>
            <div className={`p-3.5 rounded-xl ${dashboardData.net_profit >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
              <FaFileInvoiceDollar size={16} />
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-50 flex items-center gap-1.5 text-[11px] text-slate-500 font-bold">
            <span>Net Financial Margin</span>
          </div>
        </div>

        {/* Bank/Ledger Balance */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between hover:shadow-md transition duration-200">
          <div className="flex justify-between items-start">
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Account Balance</span>
              <h3 className="text-2xl font-black text-sky-600 mt-1 tracking-tight">
                ₹ {RupeeFormatWithoutDecimal(dashboardData.blnc)}
              </h3>
            </div>
            <div className="p-3.5 rounded-xl bg-sky-50 text-sky-600">
              <FaWallet size={16} />
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-slate-50 flex items-center gap-1.5 text-[11px] text-sky-600 font-bold">
            <span>Liquid Cash Assets</span>
          </div>
        </div>
      </div>

      {/* Main Charts & Analytics Block */}
      <div className="grid grid-cols-1 lg:grid-cols-7 gap-6">
        {/* Profit Loss Chart Component */}
        <div className="lg:col-span-4 bg-white border border-slate-100 shadow-sm rounded-2xl p-5 hover:shadow-md transition duration-200">
          <ProfitLossChart
            total_revenue={dashboardData?.total_revenue}
            total_expense={dashboardData?.total_expense}
            net_profit={dashboardData?.net_profit}
            onPeriodChange={handlePeriodChange}
          />
        </div>

        {/* Categories / Custom Pie Chart */}
        <div className="lg:col-span-3 bg-white border border-slate-100 shadow-sm rounded-2xl p-5 hover:shadow-md transition duration-200">
          <CustomPieChart
            coreManagement={dashboardData?.coreManagement || []}
            onPeriodChange={handlePeriodChange}
          />
        </div>
      </div>

      {/* Bottom Row: Invoices, Upcoming & Overdues */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Invoice Management Donut */}
        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-5 flex flex-col justify-between hover:shadow-md transition duration-200">
          <div>
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-4">
              Invoice Status Breakdown
            </h2>
            <div className="flex items-center justify-between gap-4 mt-2">
              <div className="w-40 h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={formattedInvoiceData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={65}
                      paddingAngle={3}
                    >
                      {formattedInvoiceData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => [`${value} Invoices`, "Count"]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Indicators */}
              <div className="flex flex-col gap-2.5 flex-1">
                {formattedInvoiceData.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs">
                    <span 
                      className="w-2.5 h-2.5 rounded-full shrink-0" 
                      style={{ backgroundColor: entry.color }}
                    ></span>
                    <span className="text-slate-600 font-bold truncate max-w-[100px]">{entry.name}</span>
                    <span className="text-slate-400 font-bold ml-auto">{entry.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Invoice Dues */}
        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-5 flex flex-col hover:shadow-md transition duration-200 h-80 overflow-y-auto">
          <div className="flex items-center gap-2 mb-4 border-b border-slate-50 pb-2">
            <FaCalendarAlt className="text-indigo-500" size={14} />
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-400">
              Upcoming Invoice Dues
            </h2>
          </div>
          
          <div className="flex flex-col gap-3">
            {(!dashboardData?.upcomingDueDates || dashboardData.upcomingDueDates.length === 0) ? (
              <p className="text-xs text-slate-400 text-center py-8">No upcoming invoices due</p>
            ) : (
              dashboardData.upcomingDueDates.map((item, index) => (
                <div key={index} className="flex flex-col p-3 rounded-xl bg-slate-50 hover:bg-slate-100/70 border border-slate-100 transition duration-150">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-slate-700">{item.customer_name || "Unknown Customer"}</span>
                    <span className="text-[10px] bg-indigo-50 text-indigo-700 font-black px-2 py-0.5 rounded">
                      {item.invoice_no}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2.5 text-[10px] text-slate-400 font-semibold">
                    <span>Due Date: {formatDate(item.due_date)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Overdue Payments */}
        <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-5 flex flex-col hover:shadow-md transition duration-200 h-80 overflow-y-auto">
          <div className="flex items-center gap-2 mb-4 border-b border-slate-50 pb-2">
            <FaExclamationTriangle className="text-rose-500" size={14} />
            <h2 className="text-xs font-black uppercase tracking-widest text-slate-400">
              Overdue Payments
            </h2>
          </div>

          <div className="flex flex-col gap-3">
            {(!dashboardData?.upcomingoverdue || dashboardData.upcomingoverdue.length === 0) ? (
              <p className="text-xs text-slate-400 text-center py-8">No overdue payments</p>
            ) : (
              dashboardData.upcomingoverdue.map((item, index) => (
                <div key={index} className="flex flex-col p-3 rounded-xl bg-rose-50/20 hover:bg-rose-50/40 border border-rose-100/50 transition duration-150">
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold text-slate-800">{item.customer_name || "Unknown Customer"}</span>
                    <span className="text-[10px] bg-rose-50 text-rose-700 font-black px-2 py-0.5 rounded">
                      {item.invoice_no}
                    </span>
                  </div>
                  <div className="flex justify-between items-center mt-2.5 text-[10px] text-rose-500 font-bold">
                    <span>Overdue Since: {formatDate(item.due_date)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default AccountsDashboard;
